// AIDEV-NOTE: Componente visual para status de sincronizacao de pedidos
// Exibe Chip MUI com icone e cor baseados no StatusPedido

import { Chip, Tooltip, type ChipProps } from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SyncIcon from '@mui/icons-material/Sync';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import type { StatusPedido } from '@azoupvendas/shared';
import type { ReactElement } from 'react';

// -- Config por status --

interface StatusVisualConfig {
  label: string;
  color: ChipProps['color'];
  icon: ReactElement;
}

const STATUS_VISUAL: Record<StatusPedido, StatusVisualConfig> = {
  rascunho: {
    label: 'Rascunho',
    color: 'default',
    icon: <EditNoteIcon fontSize="small" />
  },
  finalizado: {
    label: 'Finalizado',
    color: 'primary',
    icon: <CheckCircleOutlineIcon fontSize="small" />
  },
  sincronizando: {
    label: 'Sincronizando',
    color: 'warning',
    icon: <SyncIcon fontSize="small" />
  },
  sincronizado: {
    label: 'Sincronizado',
    color: 'success',
    icon: <CheckCircleIcon fontSize="small" />
  },
  erro_sync: {
    label: 'Erro Sync',
    color: 'error',
    icon: <ErrorOutlineIcon fontSize="small" />
  }
};

// -- CSS keyframes para animacao de rotacao continua --

const syncRotateAnimation = {
  '@keyframes syncRotate': {
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(360deg)' }
  },
  '& .MuiChip-icon': {
    animation: 'syncRotate 1.2s linear infinite'
  }
} as const;

// -- Props --

export interface SyncStatusBadgeProps {
  status: StatusPedido;
  syncError?: string | null;
  size?: 'small' | 'medium';
}

// -- Component --

export function SyncStatusBadge({ status, syncError, size = 'small' }: SyncStatusBadgeProps) {
  const config = STATUS_VISUAL[status];

  const chip = (
    <Chip
      label={config.label}
      color={config.color}
      icon={config.icon}
      size={size}
      sx={status === 'sincronizando' ? syncRotateAnimation : undefined}
    />
  );

  // AIDEV-NOTE: Se erro_sync e ha mensagem de erro, exibe Tooltip ao hover
  if (status === 'erro_sync' && syncError) {
    return (
      <Tooltip title={syncError} arrow placement="top">
        {chip}
      </Tooltip>
    );
  }

  return chip;
}
